"use client";

import { supabase } from "@/lib/supabase";
import type { DbNotification } from "@/types/db";
import { useCallback, useEffect, useId, useState } from "react";

const NOTIFICATION_COLUMNS =
  "id, user_id, type, title, body, link, entity_type, entity_id, actor_id, metadata, read_at, created_at";

const DEFAULT_LIMIT = 20;

export type UseNotificationsReturn = {
  notifications: DbNotification[];
  unreadCount: number;
  loading: boolean;
  refresh: () => void;
  markAsRead: (notificationId: string) => Promise<boolean>;
  markAllAsRead: () => Promise<boolean>;
};

/**
 * Recent notifications + unread count for the signed-in user, kept live via
 * a Supabase Realtime subscription to INSERTs on the notifications table
 * (RLS-scoped: the SELECT policy means only the user's own rows are delivered).
 *
 * Writes send ONLY { read_at } — the UPDATE grant on notifications is
 * column-scoped to read_at, so any other column in the payload would be
 * rejected outright.
 */
export function useNotifications(
  userId: string | null,
  { limit = DEFAULT_LIMIT }: { limit?: number } = {}
): UseNotificationsReturn {
  const [notifications, setNotifications] = useState<DbNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Supabase reuses a channel's underlying connection when the topic string
  // matches one already registered, instead of creating a new one — so this
  // hook can be mounted more than once at a time for the same user (sidebar
  // bell + notifications page), each instance needs its own topic or the
  // later ones crash calling `.on()` on an already-subscribed channel.
  const instanceId = useId();

  const fetchNotifications = useCallback(async () => {
    if (!userId) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    setLoading(true);

    const [listResult, countResult] = await Promise.all([
      supabase
        .from("notifications")
        .select(NOTIFICATION_COLUMNS)
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(limit),
      supabase
        .from("notifications")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId)
        .is("read_at", null),
    ]);

    if (listResult.error) {
      console.error("[useNotifications] fetch error:", listResult.error);
    }
    if (countResult.error) {
      console.error("[useNotifications] count error:", countResult.error);
    }

    setNotifications((listResult.data as DbNotification[] | null) ?? []);
    setUnreadCount(countResult.count ?? 0);
    setLoading(false);
  }, [userId, limit]);

  useEffect(() => {
    let cancelled = false;

    queueMicrotask(() => {
      if (!cancelled) void fetchNotifications();
    });

    return () => {
      cancelled = true;
    };
  }, [fetchNotifications]);

  // Realtime: prepend newly inserted notifications for this user as they land.
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel(`notifications:${userId}:${instanceId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const inserted = payload.new as DbNotification;
          setNotifications((prev) =>
            [inserted, ...prev.filter((n) => n.id !== inserted.id)].slice(0, limit)
          );
          if (!inserted.read_at) {
            setUnreadCount((count) => count + 1);
          }
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [userId, limit, instanceId]);

  const markAsRead = useCallback(
    async (notificationId: string): Promise<boolean> => {
      if (!userId) return false;

      const target = notifications.find((n) => n.id === notificationId);
      if (target?.read_at) return true; // already read — nothing to do

      const readAt = new Date().toISOString();

      // Payload must contain ONLY read_at (column-scoped UPDATE grant).
      const { error } = await supabase
        .from("notifications")
        .update({ read_at: readAt })
        .eq("id", notificationId)
        .is("read_at", null);

      if (error) {
        console.error("[useNotifications] markAsRead error:", error);
        return false;
      }

      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read_at: readAt } : n))
      );
      setUnreadCount((count) => Math.max(0, count - 1));
      return true;
    },
    [userId, notifications]
  );

  const markAllAsRead = useCallback(async (): Promise<boolean> => {
    if (!userId) return false;

    const readAt = new Date().toISOString();

    // Payload must contain ONLY read_at (column-scoped UPDATE grant).
    const { error } = await supabase
      .from("notifications")
      .update({ read_at: readAt })
      .eq("user_id", userId)
      .is("read_at", null);

    if (error) {
      console.error("[useNotifications] markAllAsRead error:", error);
      return false;
    }

    setNotifications((prev) =>
      prev.map((n) => (n.read_at ? n : { ...n, read_at: readAt }))
    );
    setUnreadCount(0);
    return true;
  }, [userId]);

  return {
    notifications,
    unreadCount,
    loading,
    refresh: fetchNotifications,
    markAsRead,
    markAllAsRead,
  };
}
